<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="false" />

    <div id="desktop-address-book" class="d-none d-md-block">
      <div class="address-book-desktop-stack">
        <BlockContainer variant="full" :title="$t('addressBook.title.addressBook')" role="region">
          <Table
            small
            :items="addresses"
            :fields="desktopFields"
            :responsive="true"
            :striped="true"
            :hover="true"
            :actions="desktopActions"
            :aria-label="$t('addressBook.ariaLabel.addressList')"
          />

          <template #actions>
            <Button v-if="!isSelectMode" @click="handleCreateAddress">{{$t('addressBook.button.newAddress')}}</Button>
            <Button v-if="isSelectMode" @click="navigateBackFromSelect">{{$t('system.button.cancel')}}</Button>
            <Button v-else-if="fromCheckout" @click="goBackToOrderFlow">{{$t('addressBook.button.backToOrderFlow')}}</Button>
          </template>
        </BlockContainer>
      </div>
    </div>

    <div id="mobile-address-book" class="d-md-none">
      <div class="address-book-mobile-stack">
        <CardList
          :items="addresses"
          :list-aria-label="$t('addressBook.ariaLabel.addressList')"
          :empty-text="$t('system.label.noData')"
          :item-aria-label="mobileItemAriaLabel"
          :item-key="mobileItemKey"
          @item-click="handleRowClicked"
        >
          <template #item="{ item, ariaLabel, onClick }">
            <div class="address-mobile-card">
              <CardListItem :aria-label="ariaLabel" @click="onClick()">
                <div>
                  <BlockContainer :rows="addressRows(item)">
                    <template #paragraph>
                      <p v-if="item.isPrimary" class="address-primary-label">{{ $t('addressBook.ariaLabel.isPrimary') }}</p>
                    </template>
                  </BlockContainer>
                </div>
              </CardListItem>
            </div>
          </template>
        </CardList>
        <div>
          <Button v-if="!isSelectMode" @click="handleCreateAddress">{{$t('addressBook.button.newAddress')}}</Button>
          <Button v-if="isSelectMode" @click="navigateBackFromSelect">{{$t('system.button.cancel')}}</Button>
          <Button v-else-if="fromCheckout" @click="goBackToOrderFlow">{{$t('addressBook.button.backToOrderFlow')}}</Button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model="showSetPrimaryDialog"
      :title="$t('addressBook.dialog.primary.title')"
      :message="$t('addressBook.dialog.primary.message')"
      :okLabel="$t('system.button.yes')"
      :cancelLabel="$t('system.button.no')"
      :okDisabled="isLoading"
      @confirm="confirmSetPrimary"
    />

    <ConfirmDialog
      v-model="showDeleteDialog"
      :title="$t('addressBook.dialog.delete.title')"
      :message="$t('addressBook.dialog.delete.message')"
      :okLabel="$t('system.button.yes')"
      :cancelLabel="$t('system.button.no')"
      :okDisabled="isLoading"
      @confirm="confirmDeleteAddress"
    />

  </div>
</template>

<script>
import Button from '@/components/actions/Button.vue';
import VueLoading from "vue-loading-overlay";
import { addressService } from '@/services/api/addressService';
import BlockContainer from "@/components/layout/BlockContainer.vue";
import {Workflow} from "@/constants/constants";
import {useAppStore} from "@/stores/app";
import Table from "@/components/data-display/Table.vue";
import CardList from "@/components/data-display/CardList.vue";
import CardListItem from "@/components/data-display/CardListItem.vue";
import ConfirmDialog from "@/components/feedback/ConfirmDialog.vue";

export default {
  name: 'AddressBook',
  setup() {
    const appStore = useAppStore();
    return { appStore };
  },
  components: {
    Table,
    CardList,
    CardListItem,
    ConfirmDialog,
    BlockContainer,
    Button,
    VueLoading
  },
  props: {
    fromCheckout: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addresses: [],
      selectedAddress: null,
      pendingAddressAction: null,
      showDeleteDialog: false,
      showSetPrimaryDialog: false,
      isLoading: false,
      error: null,
    }
  },
  computed: {
    isSelectMode() {
      return this.$route?.query?.mode === 'select';
    },
    returnRouteName() {
      return this.$route?.query?.returnTo || null;
    },
    desktopFields() {
      return [
        {
          key: 'name',
          label: this.$t('addressBook.label.name'),
          sortable: true,
          tdClass: 'text-left',
          formatter: (value, key, item) => this.desktopName(item),
          sortValue: (item) => this.desktopName(item),
        },
        {
          key: 'streetAddress',
          label: this.$t('addressBook.label.street'),
          sortable: true,
          tdClass: 'text-left',
          formatter: (value, key, item) => this.streetValue(item),
          sortValue: (item) => this.streetValue(item),
        },
        {
          key: 'city',
          label: this.$t('addressBook.label.city'),
          sortable: true,
          tdClass: 'text-left',
          formatter: (value, key, item) => this.cityValue(item),
          sortValue: (item) => this.cityValue(item),
        },
        {
          key: 'countryName',
          label: this.$t('addressBook.label.country'),
          sortable: true,
          tdClass: 'text-left',
        },
        {
          key: 'telephoneNumber',
          label: this.$t('addressBook.label.phone'),
          sortable: true,
          tdClass: 'text-left',
          formatter: (value, key, item) => this.phoneValue(item),
          sortValue: (item) => this.phoneValue(item),
        },
        {
          key: 'isPrimary',
          label: this.$t('addressBook.ariaLabel.isPrimary'),
          sortable: true,
          tdClass: 'text-left',
          formatter: (value) => value ? this.$t('text.yes') : '',
          sortValue: (item) => item.isPrimary ? 1 : 0,
        },
        {
          key: 'actions',
          label: this.$t('field.options'),
          tdClass: 'text-right',
        },
      ];
    },
    desktopActions() {
      if (this.isSelectMode) {
        return [
          {
            icon: ['fas', 'circle-check'],
            tooltip: () => this.$t('system.button.select'),
            ariaLabel: (item) => `${this.$t('system.button.select')}: ${this.desktopName(item)}`,
            handler: (item) => this.selectAddressAndReturn(item.id || item.addrId),
            show: () => true,
            class: 'address-book-select-button',
          },
        ];
      }

      return [
        {
          icon: ['fas', 'circle-check'],
          tooltip: () => this.$t('addressBook.ariaLabel.setPrimary'),
          ariaLabel: (item) => `${this.$t('addressBook.ariaLabel.setPrimary')}: ${this.desktopName(item)}`,
          handler: (item) => this.promptSetPrimary(item),
          show: (item) => !item.isPrimary,
          class: 'address-book-set-primary-button',
        },
        {
          icon: ['fas', 'pencil'],
          tooltip: () => this.$t('addressBook.ariaLabel.edit'),
          ariaLabel: (item) => `${this.$t('addressBook.ariaLabel.edit')}: ${this.desktopName(item)}`,
          handler: (item) => this.editAddress(item.id || item.addrId),
          show: () => true,
          class: 'address-book-edit-button',
        },
        {
          icon: ['fas', 'trash-can'],
          tooltip: () => this.$t('addressBook.button.delete'),
          ariaLabel: (item) => `${this.$t('addressBook.button.delete')}: ${this.desktopName(item)}`,
          handler: (item) => this.promptDeleteAddress(item),
          show: (item) => !item.isPrimary,
          class: 'address-book-delete-button',
        },
      ];
    },
  },
  methods: {
    desktopName(item) {
      return [item.firstName, item.lastName].filter(Boolean).join(' ').trim() || item.formattedAddressWithoutCData || '';
    },
    streetValue(item) {
      return item.streetAddress || item.streetAddress1 || '';
    },
    cityValue(item) {
      return [item.postcode, item.city].filter(Boolean).join(' ').trim();
    },
    phoneValue(item) {
      return item.telephoneNumber || item.telephoneNumber1 || '';
    },
    async fetchAddresses() {
      this.isLoading = true;
      this.error = null;
      try {
        const result = await addressService.getAll();
        if (result.exception !== undefined) {
          this.error = this.$t('addressBook.messages.error.loadAddresses');
          this.addresses = [];
        } else {
          this.addresses = [...(result.addresses || [])];
          // Select primary or first address
          this.selectedAddress = [...this.addresses].sort((a,b)=>b.isPrimary-a.isPrimary)[0] || null;
        }
      } catch (e) {
        this.error = e.message || 'Error loading addresses';
        this.addresses = [];
      }
      this.isLoading = false;
    },
    async handleRowClicked(addressOrId) {
      const addressId = addressOrId && typeof addressOrId === 'object'
        ? (addressOrId.id || addressOrId.addrId)
        : addressOrId;

      if (!addressId) {
        return;
      }

      if (this.isSelectMode) {
        await this.selectAddressAndReturn(addressId);
        return;
      }

      this.$navigateToEditAddress(addressId);
    },
    promptSetPrimary(address) {
      this.pendingAddressAction = address || null;
      this.showSetPrimaryDialog = true;
    },
    promptDeleteAddress(address) {
      this.pendingAddressAction = address || null;
      this.showDeleteDialog = true;
    },
    async confirmSetPrimary() {
      const addressId = this.pendingAddressAction?.id || this.pendingAddressAction?.addrId;
      if (!addressId) {
        this.showSetPrimaryDialog = false;
        return;
      }

      this.isLoading = true;
      try {
        const addressDetails = await addressService.getById(addressId);
        const result = await addressService.setAsPrimary(addressDetails);
        if (result?.exception !== undefined) {
          this.makeToast('danger', this.$t('addressBook.messages.error.setPrimaryAddress'));
          return;
        }

        this.makeToast('success', this.$t('addressBook.messages.info.setPrimaryAddress'));
        await this.fetchAddresses();
      } catch (e) {
        this.makeToast('danger', this.$t('addressBook.messages.error.setPrimaryAddress'));
      } finally {
        this.showSetPrimaryDialog = false;
        this.pendingAddressAction = null;
        this.isLoading = false;
      }
    },
    async confirmDeleteAddress() {
      const addressId = this.pendingAddressAction?.id || this.pendingAddressAction?.addrId;
      if (!addressId) {
        this.showDeleteDialog = false;
        return;
      }

      this.isLoading = true;
      try {
        const result = await addressService.deleteById(addressId);
        if (result?.exception !== undefined) {
          this.makeToast('danger', this.$t('addressBook.messages.error.deleteAddress'));
          return;
        }

        this.makeToast('success', this.$t('addressBook.messages.info.deleteAddress'));
        await this.fetchAddresses();
      } catch (e) {
        this.makeToast('danger', this.$t('addressBook.messages.error.deleteAddress'));
      } finally {
        this.showDeleteDialog = false;
        this.pendingAddressAction = null;
        this.isLoading = false;
      }
    },
    mobileItemAriaLabel(item) {
      const primaryLabel = item.isPrimary ? this.$t('addressBook.ariaLabel.isPrimary') + ', ' : '';
      const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
      const summary = [item.city, item.countryName].filter(Boolean).join(' / ');
      return [primaryLabel, name, summary].filter(Boolean).join(' ');
    },
    mobileItemKey(item, index) {
      return item.id || item.guid || index;
    },
    addressRows(item) {
      const rows = [
        { key: 'name', label: this.$t('addressBook.label.name'), value: [item.firstName, item.lastName].filter(Boolean).join(' ') },
        { key: 'street', label: this.$t('addressBook.label.street'), value: item.streetAddress || item.streetAddress1 },
        { key: 'city', label: this.$t('addressBook.label.city'), value: [item.postcode, item.city].filter(Boolean).join(' ') },
        { key: 'country', label: this.$t('addressBook.label.country'), value: item.countryName },
        { key: 'phone', label: this.$t('addressBook.label.phone'), value: item.telephoneNumber || item.telephoneNumber1 },
      ];

      const filteredRows = rows.filter((row) => !!row.value);
      if (filteredRows.length > 0) {
        return filteredRows;
      }

      // Fallback: show backend-formatted address as plain text if structured fields are empty.
      const formattedFallback = (item.formattedAddressWithoutCData || '')
        .replace(/<br\s*\/?>/gi, ', ')
        .replace(/\s+/g, ' ')
        .trim();

      return formattedFallback
        ? [{ key: 'address', label: this.$t('ariaLabel.address'), value: formattedFallback }]
        : [];
    },
    async selectAddressAndReturn(addressId) {
      this.isLoading = true;
      try {
        const result = await addressService.changeById(addressId);
        if (result?.exception !== undefined || result?.errorCode < 0) {
          this.makeToast('danger', this.$t('addressBook.messages.error.selectAddress'));
          return;
        }

        this.appStore.setFlashToast({
          variant: 'success',
          message: this.$t('addressBook.messages.info.selectAddress'),
        });
        this.navigateBackFromSelect(addressId);
      } catch (e) {
        this.makeToast('danger', this.$t('addressBook.messages.error.selectAddress'));
      } finally {
        this.isLoading = false;
      }
    },
    navigateBackFromSelect(selectedAddressId = null) {
      if (this.returnRouteName) {
        const query = selectedAddressId ? { selectedAddressId: String(selectedAddressId) } : {};
        this.$navigate(this.returnRouteName, { withReplace: true, query });
        return;
      }
      this.goBackToOrderFlow();
    },
    handleCreateAddress() {
      this.$navigateToCreateAddress();
    },
    editAddress(addressId) {
      this.$navigateToEditAddress(addressId);
    },
    goBackToOrderFlow() {
      if (this.appStore.setWorkflow(Workflow.MEAL_PLAN)) {
        this.$navigateToMealPlanCheckout();
      } else {
        this.$navigateToOrderMenuCheckout();
      }
    }
  },
  async mounted() {
    await this.fetchAddresses();

    const flashToast = this.appStore.consumeFlashToast();
    if (flashToast?.variant && flashToast?.message) {
      this.makeToast(flashToast.variant, flashToast.message);
    }
  }
}
</script>

<style scoped>
.address-mobile-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.address-primary-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
}



.address-book-desktop-stack,
.address-book-mobile-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
